(() => {
  const SECTION_SELECTOR = '[data-wbc-coffee-compare]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const SWIPE_THRESHOLD = 40;
  const DRAG_Y_THRESHOLD = 36;
  const TRACK_EVENT_NAMESPACE = 'wbc:compare:track';

  const toInt = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const truthy = (value) => String(value).toLowerCase() === 'true';

  const shuffle = (array) => {
    const list = array.slice();
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = list[i];
      list[i] = list[j];
      list[j] = temp;
    }
    return list;
  };

  const showToast = (root, message) => {
    const toast = root.querySelector('[data-wbc-compare-toast]');
    if (!toast || !message) return;

    if (toast._hideTimer) {
      clearTimeout(toast._hideTimer);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');
    toast._hideTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2200);
  };

  const track = (root, eventSuffix, payload = {}) => {
    if (!truthy(root.dataset.trackingEnabled)) return;

    const prefix = root.dataset.trackingPrefix || 'wbc_compare';
    const eventName = `${prefix}_${eventSuffix}`;
    const eventPayload = {
      event: eventName,
      section_id: root.dataset.sectionId || '',
      context_product_id: root.dataset.contextProductId || '',
      context_product_handle: root.dataset.contextProductHandle || '',
      ...payload,
    };

    document.dispatchEvent(new CustomEvent(TRACK_EVENT_NAMESPACE, { detail: eventPayload }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(eventPayload);
    }

    if (
      window.ShopifyAnalytics &&
      window.ShopifyAnalytics.lib &&
      typeof window.ShopifyAnalytics.lib.track === 'function'
    ) {
      try {
        window.ShopifyAnalytics.lib.track(eventName, eventPayload);
      } catch (error) {
        // no-op in preview/fallback modes
      }
    }
  };

  const openDrawerViaSyntheticTrigger = ({ handle, title, image, url }) => {
    if (!handle || !document.body) return false;
    if (!document.getElementById('wb-quick-add-drawer')) return false;

    const syntheticTrigger = document.createElement('button');
    syntheticTrigger.type = 'button';
    syntheticTrigger.hidden = true;
    syntheticTrigger.setAttribute('data-wb-quick-add', '');
    syntheticTrigger.dataset.wbProductHandle = handle;
    syntheticTrigger.dataset.wbProductTitle = title || '';
    syntheticTrigger.dataset.wbProductImage = image || '';
    syntheticTrigger.dataset.wbProductUrl = url || `/products/${handle}`;

    document.body.appendChild(syntheticTrigger);
    syntheticTrigger.click();
    syntheticTrigger.remove();
    return true;
  };

  const quickAdd = async (root, card, source = 'card_button') => {
    if (!card) return;

    const handle = card.dataset.productHandle || '';
    const title = card.dataset.productTitle || '';
    const image = card.dataset.productImage || '';
    const url = card.dataset.productUrl || (handle ? `/products/${handle}` : '/');
    const variantId = toInt(card.dataset.variantId, 0);
    const available = truthy(card.dataset.available);

    track(root, 'quick_add_click', {
      source,
      target_product_handle: handle,
      target_variant_id: variantId,
      available,
    });

    if (!available) {
      showToast(root, root.dataset.toastUnavailable || 'This coffee is currently unavailable');
      track(root, 'quick_add_unavailable', {
        source,
        target_product_handle: handle,
      });
      return;
    }

    if (window.WBQuickAddDrawer && typeof window.WBQuickAddDrawer.openByHandle === 'function' && handle) {
      try {
        const trigger = {
          dataset: {
            wbProductHandle: handle,
            wbProductTitle: title,
            wbProductImage: image,
            wbProductUrl: url,
          },
        };
        await window.WBQuickAddDrawer.openByHandle(handle, trigger);
        track(root, 'quick_add_drawer_open', {
          source,
          target_product_handle: handle,
        });
        return;
      } catch (error) {
        // fall through to synthetic trigger/direct add
      }
    }

    if (openDrawerViaSyntheticTrigger({ handle, title, image, url })) {
      track(root, 'quick_add_drawer_open', {
        source,
        target_product_handle: handle,
      });
      return;
    }

    if (!variantId) {
      window.location.href = url;
      return;
    }

    try {
      const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed with status ${response.status}`);
      }

      let cartPayload = null;
      try {
        cartPayload = await response.json();
      } catch (error) {
        cartPayload = null;
      }

      showToast(root, root.dataset.toastAdded || 'Added to basket');
      track(root, 'quick_add_success', {
        source,
        target_product_handle: handle,
        target_variant_id: variantId,
      });

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wbc-compare',
            productVariantId: variantId,
            cartData: cartPayload,
          });
        } catch (error) {
          // silent fallback
        }
      }
    } catch (error) {
      showToast(root, root.dataset.toastError || 'Unable to add to basket');
      track(root, 'quick_add_error', {
        source,
        target_product_handle: handle,
        target_variant_id: variantId,
      });
    }
  };

  const initSection = (root) => {
    if (!root || root.dataset.wbcCompareReady === 'true') return;
    root.dataset.wbcCompareReady = 'true';

    const toggleButton = root.querySelector('[data-wbc-compare-toggle]');
    const panel = root.querySelector('[data-wbc-compare-panel]');
    const trackEl = root.querySelector('[data-wbc-compare-track]');
    const sliderWrap = root.querySelector('[data-wbc-compare-slider-wrap]');
    const dotsWrap = root.querySelector('[data-wbc-compare-dots]');

    if (!panel || !trackEl) return;

    const randomCount = Math.max(0, toInt(root.dataset.randomCount, 2));

    const allCards = Array.from(root.querySelectorAll('[data-wbc-compare-card]'));
    const currentCard =
      root.querySelector('[data-wbc-compare-card][data-wbc-current-card="true"]') || allCards[0] || null;
    const candidateCards = allCards.filter((card) => card.dataset.wbcCandidateCard === 'true');

    // Pick random candidates per page load and hide the rest.
    if (candidateCards.length) {
      const chosen = new Set(shuffle(candidateCards).slice(0, Math.min(randomCount, candidateCards.length)));
      candidateCards.forEach((card) => {
        const keep = chosen.has(card);
        card.hidden = !keep;
      });
    }

    let cards = Array.from(root.querySelectorAll('[data-wbc-compare-card]')).filter((card) => !card.hidden);
    if (!cards.length && currentCard) {
      currentCard.hidden = false;
      cards = [currentCard];
    }
    if (!cards.length) return;

    cards.forEach((card, index) => {
      card.dataset.cardIndex = String(index + 1);
    });

    const visibleCount = cards.length;
    trackEl.classList.remove('wbc-compare__track--count-1', 'wbc-compare__track--count-2', 'wbc-compare__track--count-3');
    trackEl.classList.add(`wbc-compare__track--count-${Math.min(3, Math.max(1, visibleCount))}`);

    const dots = Array.from(root.querySelectorAll('[data-wbc-compare-dot]'));
    dots.forEach((dot, index) => {
      dot.hidden = index >= visibleCount;
    });
    if (dotsWrap) {
      dotsWrap.hidden = visibleCount <= 1;
      dotsWrap.setAttribute('aria-valuemax', String(Math.max(1, visibleCount)));
    }

    const mobileQuery = window.matchMedia(MOBILE_QUERY);

    const getVisibleSlots = () => {
      if (window.innerWidth <= 749) return 1;
      return Math.min(3, cards.length);
    };

    const getPageCount = () => {
      const slots = Math.max(1, getVisibleSlots());
      return Math.max(1, cards.length - slots + 1);
    };

    let selectedCard = cards.find((card) => card.classList.contains('is-selected')) || cards[0];
    let currentPage = Math.max(1, cards.indexOf(selectedCard) + 1);
    let touchStartX = 0;
    let touchStartY = 0;
    let mouseStartX = 0;
    let mouseStartY = 0;
    let mouseIsDown = false;
    let viewTracked = false;

    const updateDots = () => {
      if (!dots.length) return;
      const pageCount = getPageCount();
      dots.forEach((dot, index) => {
        dot.hidden = index >= pageCount;
        if (dot.hidden) return;
        dot.classList.toggle('is-active', index + 1 === currentPage);
      });
      if (dotsWrap) {
        dotsWrap.hidden = pageCount <= 1;
        dotsWrap.setAttribute('aria-valuemax', String(pageCount));
        dotsWrap.setAttribute('aria-valuenow', String(currentPage));
      }
    };

    const slideStep = () => {
      if (cards.length <= 1) return cards[0].getBoundingClientRect().width;
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      return step > 0 ? step : cards[0].getBoundingClientRect().width;
    };

    const goToPage = (nextPage, source = null) => {
      const pageCount = getPageCount();
      const clamped = Math.max(1, Math.min(nextPage, pageCount));
      currentPage = clamped;

      if (cards.length > getVisibleSlots()) {
        trackEl.classList.add('is-slider');
        const offset = (currentPage - 1) * slideStep();
        trackEl.style.transform = `translateX(${-offset}px)`;

        const firstCard = cards[0];
        if (firstCard) {
          const gap = Number.parseFloat(getComputedStyle(trackEl).columnGap || getComputedStyle(trackEl).gap || '12') || 12;
          const wrapWidth = sliderWrap ? sliderWrap.clientWidth : firstCard.getBoundingClientRect().width;
          const slots = getVisibleSlots();
          const widthPerCard = slots <= 1 ? wrapWidth : (wrapWidth - gap * (slots - 1)) / slots;
          trackEl.style.setProperty('--wbc-compare-slide-width', `${Math.max(140, widthPerCard)}px`);
        }
      } else {
        trackEl.classList.remove('is-slider');
        trackEl.style.transform = '';
        trackEl.style.removeProperty('--wbc-compare-slide-width');
      }

      updateDots();

      if (source) {
        track(root, 'slide_change', {
          source,
          page: currentPage,
        });
      }
    };

    const setSelectedCard = (card, source = null) => {
      if (!card) return;

      selectedCard = card;
      cards.forEach((item) => {
        const isSelected = item === selectedCard;
        item.classList.toggle('is-selected', isSelected);
        item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      const selectedIndex = cards.indexOf(selectedCard) + 1;
      const slots = getVisibleSlots();
      const page = Math.max(1, Math.min(selectedIndex - slots + 1, selectedIndex));
      goToPage(page, null);

      if (source) {
        track(root, 'card_select', {
          source,
          selected_position: selectedIndex,
          selected_product_handle: selectedCard.dataset.productHandle || '',
        });
      }
    };

    const setPanelOpen = (isOpen, source = null) => {
      if (!toggleButton) return;

      toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      panel.classList.toggle('is-open', isOpen);
      panel.hidden = !isOpen;

      if (source) {
        track(root, isOpen ? 'panel_open' : 'panel_close', {
          source,
          visible_cards: cards.length,
        });
      }

      if (isOpen && mobileQuery.matches) {
        goToPage(cards.indexOf(selectedCard) + 1, null);
      }
    };

    cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('button,a,input,select,textarea')) return;
        setSelectedCard(card, 'card_click');
      });

      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setSelectedCard(card, 'card_keydown');
      });
    });

    if (toggleButton) {
      toggleButton.addEventListener('click', () => {
        const expanded = toggleButton.getAttribute('aria-expanded') === 'true';
        setPanelOpen(!expanded, 'toggle_button');
      });
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (dot.hidden) return;
        const nextCard = cards[index];
        if (nextCard) {
          setSelectedCard(nextCard, 'dot_click');
        }
      });
    });

    if (sliderWrap) {
      sliderWrap.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        },
        { passive: true }
      );

      sliderWrap.addEventListener(
        'touchend',
        (event) => {
          if (cards.length <= 1) return;

          const touch = event.changedTouches[0];
          const deltaX = touch.clientX - touchStartX;
          const deltaY = Math.abs(touch.clientY - touchStartY);

          if (Math.abs(deltaX) < SWIPE_THRESHOLD || deltaY > DRAG_Y_THRESHOLD) return;

          if (deltaX < 0) {
            goToPage(currentPage + 1, 'swipe_left');
          } else {
            goToPage(currentPage - 1, 'swipe_right');
          }

          const nextCard = cards[currentPage - 1];
          if (nextCard) {
            setSelectedCard(nextCard, 'swipe_select');
          }
        },
        { passive: true }
      );

      sliderWrap.addEventListener('mousedown', (event) => {
        mouseIsDown = true;
        mouseStartX = event.clientX;
        mouseStartY = event.clientY;
      });

      sliderWrap.addEventListener('mouseleave', () => {
        mouseIsDown = false;
      });

      window.addEventListener('mouseup', (event) => {
        if (!mouseIsDown || cards.length <= 1) return;
        mouseIsDown = false;

        const deltaX = event.clientX - mouseStartX;
        const deltaY = Math.abs(event.clientY - mouseStartY);

        if (Math.abs(deltaX) < SWIPE_THRESHOLD || deltaY > DRAG_Y_THRESHOLD) return;

        if (deltaX < 0) {
          goToPage(currentPage + 1, 'mouse_drag_left');
        } else {
          goToPage(currentPage - 1, 'mouse_drag_right');
        }

        const nextCard = cards[currentPage - 1];
        if (nextCard) {
          setSelectedCard(nextCard, 'mouse_drag_select');
        }
      });

      let wheelLocked = false;
      sliderWrap.addEventListener(
        'wheel',
        (event) => {
          if (cards.length <= getVisibleSlots()) return;

          const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
          if (Math.abs(delta) < 10 || wheelLocked) return;

          event.preventDefault();
          wheelLocked = true;

          if (delta > 0) {
            goToPage(currentPage + 1, 'wheel_next');
          } else {
            goToPage(currentPage - 1, 'wheel_prev');
          }

          const nextCard = cards[currentPage - 1];
          if (nextCard) {
            setSelectedCard(nextCard, 'wheel_select');
          }

          window.setTimeout(() => {
            wheelLocked = false;
          }, 220);
        },
        { passive: false }
      );
    }

    root.addEventListener('click', (event) => {
      const quickAddButton = event.target.closest('[data-wbc-compare-quick-add]');
      if (quickAddButton) {
        event.preventDefault();
        const card = quickAddButton.closest('[data-wbc-compare-card]');
        if (card) {
          setSelectedCard(card, 'quick_add_select');
          quickAdd(root, card, 'card_button');
        }
        return;
      }

      const viewLink = event.target.closest('[data-wbc-compare-view]');
      if (viewLink) {
        const card = viewLink.closest('[data-wbc-compare-card]');
        if (card) {
          setSelectedCard(card, 'view_link_select');
          track(root, 'view_product_click', {
            target_product_handle: card.dataset.productHandle || '',
            target_position: cards.indexOf(card) + 1,
          });
        }
      }
    });

    const onViewportChange = () => {
      const selectedIndex = cards.indexOf(selectedCard) + 1;
      const slots = getVisibleSlots();
      const page = Math.max(1, Math.min(selectedIndex - slots + 1, selectedIndex));
      goToPage(page, null);
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onViewportChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(onViewportChange);
    }

    setSelectedCard(selectedCard, null);
    setPanelOpen(truthy(root.dataset.openDefault), null);

    const onView = () => {
      if (viewTracked) return;
      viewTracked = true;
      track(root, 'view', {
        visible_cards: cards.length,
      });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            onView();
            obs.disconnect();
          });
        },
        { threshold: 0.35 }
      );
      observer.observe(root);
    } else {
      onView();
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
